import {
  CreateTreeStateOptions,
  CreatedTreeState,
  ExpandedMap,
  Key,
  NodeValues,
  NodeValuesMap,
  SelectedMap,
  StateChangeMap,
  TreeErrorsMap,
  TreeNode,
  TreeNodesMap,
} from "./types"
import {
  _ROOT_,
  allChildren,
  arrayMove,
  assertKey,
  buildTreeNodesMap,
  getSiblingNodes,
  insertAtIndexInline,
} from "./helpers"
import { proxy, subscribe, useSnapshot } from "valtio"

import { cloneDeep } from "lodash"

type LastSelectedKey = {
  last: null | Key
}

export function createTreeState<T>(
  opts: CreateTreeStateOptions<T>,
): CreatedTreeState<T> {
  const {
    getKey,
    getParentKey,
    setOrder,
    compare,
    mergeParentProperties,
    initialExpandedKeys,
    isNew,
  } = opts

  const _compare = (a: Key, b: Key) => {
    if (compare) {
      return compare(state[a], state[b])
    } else {
      return 0
    }
  }

  let state = proxy<NodeValuesMap<T>>({})
  /**
   * Tracks state change
   */
  let stateChange = proxy<StateChangeMap>({ lastUpdated: Date.now() })
  let removed = proxy<NodeValuesMap<T>>({})
  let tree = proxy<TreeNodesMap>({})
  let expanded = proxy<ExpandedMap>(initialExpandedKeys || {})
  let selected = proxy<SelectedMap>({})
  let errors = proxy<TreeErrorsMap>({})

  /**
   * Subscribe to the whole state object and update "lastUpdated" value in "stateChange"
   */
  subscribe(state, () => (stateChange.lastUpdated = Date.now()))

  let clipboardItems = proxy<Record<string, string>>({})

  function clearClipboardItems() {
    Object.keys(clipboardItems).forEach((k) => {
      delete clipboardItems[k]
    })
  }

  function addClipboardItems(keys: Key[]) {
    const prev = Object.keys(clipboardItems).length
    if (prev) {
      clearClipboardItems()
      keys.forEach((key) => {
        clipboardItems[key] = key
      })
    } else {
      keys.forEach((key) => {
        clipboardItems[key] = key
      })
    }
  }

  function getClipboardItems() {
    return Object.keys(clipboardItems)
  }

  let lastState = proxy<LastSelectedKey>({ last: null })
  const setLastKey = (key: Key | null) => {
    lastState.last = key
  }

  function useLastKey() {
    const s = useSnapshot(lastState)
    return [s.last, setLastKey] as const
  }

  function setErrors(errorsIn: TreeErrorsMap) {
    Object.keys(errorsIn).forEach((ek) => (errors[ek] = errorsIn[ek]))
  }

  function clearErrors() {
    Object.keys(errors).forEach((key) => {
      delete errors[key]
    })
  }

  function clearError(id: string) {
    delete errors[id]
  }

  function clearSelections() {
    Object.keys(selected).forEach((key) => {
      delete selected[key]
    })
    setLastKey(null)
  }

  function clearRemoved() {
    Object.keys(removed).forEach((key) => {
      delete removed[key]
    })
  }

  function contract() {
    Object.keys(expanded).forEach((key) => {
      delete expanded[key]
    })
  }

  function clearState() {
    removeAll()
  }

  function resetAll(keepExpanded?: boolean, keepErrors?: boolean) {
    clearState()
    clearSelections()
    !keepErrors && clearErrors()
    clearRemoved()
    !keepExpanded && contract()
    clearClipboardItems()
  }

  function initialize(
    items: T[],
    keepExpanded?: boolean,
    keepErrors?: boolean,
  ) {
    resetAll(keepExpanded, keepErrors)
    // state
    items.forEach((it) => {
      const key = getKey(it)
      assertKey(key)
      state[key] = it
    })

    // tree
    const ns = buildTreeNodesMap<T>(
      items,
      getKey,
      getParentKey,
      compare && _compare,
    )
    for (let [key, node] of Object.entries(ns)) {
      tree[key] = node
      if (node.parentId && mergeParentProperties) {
        state[key] = mergeParentProperties(state[key], state[node.parentId])
      }
    }
  }

  function applyOrder(orderedKeys: Key[]) {
    if (!setOrder) return
    orderedKeys.forEach((key, index) => {
      const it = state[key]
      setOrder(it, index)
    })
  }

  function removeAll(): void {
    Object.keys(state).forEach((key) => {
      delete state[key]
    })
    Object.keys(tree).forEach((key) => {
      delete tree[key]
    })
  }

  function removeNodes(nodes?: TreeNode[]): void {
    // clone the proxies
    let updatedState = cloneDeep(state)
    let updatedTree = cloneDeep(tree)
    let updatedRemoved = cloneDeep(removed)
    let updatedSelected = cloneDeep(selected)
    let updatedExpanded = cloneDeep(expanded)

    nodes?.forEach((node) => {
      if (node) {
        const { id, parentId, children } = node

        // 1. remove it from parent
        if (parentId) {
          let cs = updatedTree[parentId]?.children
          if (cs) {
            const parentChildren = updatedTree[parentId]?.children
            if (!!parentChildren?.length) {
              const updatedChildren = [
                ...parentChildren.slice(0, cs.indexOf(id)),
                ...parentChildren.slice(cs.indexOf(id) + 1),
              ]
              updatedTree[parentId].children = updatedChildren
            }
          }
        }

        // 2. remove it's clildren
        if (children) {
          children.forEach((childId) => {
            delete updatedTree[childId]
            delete updatedState[childId]
          })
        }

        // 3. remove it
        delete updatedTree[id]

        if (isNew && !isNew(updatedState[id])) {
          updatedRemoved[id] = updatedState[id]
        }
        delete updatedState[id]

        // 4. remove selected
        delete updatedSelected[id]

        // 4. remove expanded
        delete updatedExpanded[id]
      }
    })
    // update the original proxies with the updated clones
    clearState()
    clearRemoved()
    clearSelections()

    Object.assign(state, updatedState)
    Object.assign(tree, updatedTree)
    Object.assign(removed, updatedRemoved)
    Object.assign(expanded, updatedExpanded)
    Object.assign(selected, updatedSelected)
  }

  /*
    Normalize the `removed` collection in order to
    avoid including nodes that belong to removed parents
    or packages
  */
  function normalizeRemoved() {
    const removedKeys = Object.keys(removed)
    if (!removedKeys.length) return removed

    removedKeys.forEach((k: string) => {
      const removedNode = removed[k] as any
      const parentId = removedNode.parentId
      const packageId = removedNode.packageId
      const hasRemovedParent = parentId && removed[parentId]
      const isInRemovedPackage = packageId && removed[packageId]
      if (hasRemovedParent || isInRemovedPackage) {
        delete removed[k]
      }
    })

    return removed
  }

  function removeBranch(key: string) {
    const nodesToRemove = [...getAllChildren(key), key]
    remove(...nodesToRemove)
  }

  function remove(...keys: Key[]): void {
    const nodes = keys.map((key) => {
      return tree[key]
    })
    removeNodes(nodes)
    normalizeRemoved()
  }

  function addNodeToParent(
    parentKey: Key | undefined,
    item: NodeValues<T>,
    position: "start" | "end" | number,
  ) {
    const parentId = parentKey || _ROOT_
    const parent = tree[parentId]
    if (!parent) return

    const key = getKey(item)
    assertKey(key)
    state[key] = item

    let new_node: TreeNode = {
      id: key,
      parentId: parentId,
      children: null,
      depth: parent.depth + 1,
    }

    if (parent.children) {
      //
      if (position === "end") {
        parent.children.push(key)
      } else if (position === "start") {
        parent.children.unshift(key)
      } else {
        let index = position
        insertAtIndexInline(parent.children, index, [key])
      }
      //
    } else {
      parent.children = [key]
    }
    applyOrder(parent.children)
    tree[parent.id] = parent
    tree[key] = new_node
    parentId !== _ROOT_ && (expanded[parentId] = true)
  }

  function append(
    parentKey: Key | undefined,
    ...values: NodeValues<T>[]
  ): void {
    values.forEach((v) => {
      addNodeToParent(parentKey, v, "end")
    })
  }

  function prepend(
    parentKey: Key | undefined,
    ...values: NodeValues<T>[]
  ): void {
    values.forEach((v) => {
      addNodeToParent(parentKey, v, "start")
    })
  }

  function insert(
    parentKey: Key | undefined,
    position: "start" | "end" | number,
    ...values: NodeValues<T>[]
  ): void {
    values.forEach((v) => {
      addNodeToParent(parentKey, v, position)
    })
  }

  function replace(
    item: NodeValues<T>,
    parentKey?: Key,
    position?: number,
  ): void {
    const key = getKey(item)
    assertKey(key)
    if (state[key]) {
      state[key] = item
    } else {
      parentKey && addNodeToParent(parentKey, item, position || "start")
      delete removed[key]
    }
  }

  function replaceWithTree(
    oldKey: Key,
    root: NodeValues<T>,
    children: Array<NodeValues<T>>,
    parentKey?: Key,
    position?: number,
    keep: boolean = false,
  ): void {
    const rootKey = getKey(root)
    assertKey(rootKey)

    // replace root
    !keep && removeBranch(oldKey)
    keep
      ? replace(root, rootKey, position)
      : insert(parentKey, position || "start", root)

    // insert children to state
    children.forEach((it) => {
      const key = getKey(it)
      assertKey(key)
      state[key] = it
    })

    // tree
    const initialDepth = 0
    const ns = buildTreeNodesMap<T>(
      [root, ...children],
      getKey,
      getParentKey,
      compare && _compare,
      rootKey,
      initialDepth,
    )

    for (let [key, node] of Object.entries(ns)) {
      tree[key] = node
      if (node.parentId && mergeParentProperties) {
        expanded[node.parentId] = true
        state[key] = mergeParentProperties(state[key], state[node.parentId])
      }
    }
  }

  function relativeItemOrder(parentKey: Key, key: Key): number | undefined {
    const cs = tree[parentKey]?.children
    if (!cs) return undefined
    const i = cs.indexOf(key)
    return i > -1 ? i : undefined
  }

  function reorderChildrenOf(
    parentKey: Key | undefined,
    from: number,
    to: number,
  ) {
    const parentId = parentKey || _ROOT_
    const parent = tree[parentId]
    if (!parent) return
    if (!parent.children) return
    const reordered = arrayMove(parent.children, from, to)
    tree[parentId].children = reordered
    applyOrder(reordered)
  }

  function reorderSiblingsOf(nodeKey: Key, from: number, to: number) {
    const node = tree[nodeKey]
    if (!node) return
    reorderChildrenOf(node.parentId, from, to)
  }

  function getSiblings(nodeKey: Key): string[] {
    const node = tree[nodeKey]
    return getSiblingNodes(node, tree)
  }
  function areSiblings(keyA: Key, keyB: Key): boolean {
    const parentA = tree[keyA]?.parentId
    const parentB = tree[keyB]?.parentId
    return parentA === parentB
  }

  function getAllChildren(nodeKey: Key): Key[] {
    return allChildren(nodeKey, tree)
  }

  return {
    state,
    stateChange,
    tree,
    removed,

    initialize,
    insert,
    append,
    prepend,
    remove,
    replace,
    replaceWithTree,

    expanded,
    contract,

    selected,
    clearSelections,

    errors,
    setErrors,
    clearError,
    clearErrors,

    reorderSiblingsOf,
    getSiblings,
    areSiblings,
    getAllChildren,
    //
    useLastKey,
    //
    getClipboardItems,
    addClipboardItems,
    clearClipboardItems,
    relativeItemOrder,
  }
}
